import { NotificationPreferences } from '../../types';
import api from './client';

// Feature Flags API (no auth required)
export const featuresAPI = {
  // Accepts optional orgId (public_id) for per-org feature overrides
  getFeatures: (orgId?: string) => api.get('/features', { params: orgId ? { orgId } : undefined }),
};

// Site Banner API (no auth required)
export const siteBannerAPI = {
  getActive: () => api.get('/site-banner'),
};

// Notification API
export const notificationAPI = {
  getAll: (unreadOnly?: boolean) =>
    api.get('/notifications', { params: { unreadOnly } }),

  getArchived: () =>
    api.get('/notifications/archived'),

  markAsRead: (notificationId: number) =>
    api.put(`/notifications/${notificationId}/read`),

  markAllAsRead: () => api.put('/notifications/read-all'),

  // Spec 298 follow-up: mark only the given notification type(s) read (e.g.
  // fired on visiting /connect or /chronicle) — narrower than markAllAsRead.
  // Optional channelId (2026-08-07 channel-scoped mark-read follow-up) is the
  // active channel's public_id UUID — only meaningful alongside
  // channel_post_reply/channel_mention; omitted (undefined), it's dropped by
  // JSON.stringify and the backend behaves exactly as before this param
  // existed (required for the Chronicle/content_shared caller).
  markByTypeAsRead: (types: string[], channelId?: string) =>
    api.put('/notifications/read-by-type', { types, channelId }),

  archive: (notificationId: number) =>
    api.put(`/notifications/${notificationId}/archive`),

  unarchive: (notificationId: number) =>
    api.put(`/notifications/${notificationId}/unarchive`),

  archiveAllRead: () => api.put('/notifications/archive-all-read'),

  delete: (notificationId: number) =>
    api.delete(`/notifications/${notificationId}`),

  // Notification preferences
  getPreferences: () => api.get('/notifications/preferences'),

  updatePreferences: (preferences: Partial<NotificationPreferences>) =>
    api.put('/notifications/preferences', preferences),
};

// Inbox API (task-based notifications)
export const inboxAPI = {
  // Get all inbox items (tasks with unread activity)
  getInbox: () => api.get('/inbox'),

  // Get just the unread count (for navbar badge)
  getUnreadCount: () => api.get('/inbox/count'),

  // Mark a specific task as read
  markTaskRead: (taskId: string) => api.post(`/inbox/${taskId}/read`),

  // Mark all tasks as read
  markAllRead: () => api.post('/inbox/mark-all-read'),
};

// Device Token API (for Firebase FCM push notifications — fallback)
export const deviceTokenAPI = {
  register: (token: string, platform: 'ios' | 'android' | 'web', deviceName?: string) =>
    api.post('/device-tokens/register', { token, platform, deviceName }),

  // authTokenOverride: explicit Bearer token to authenticate with, bypassing
  // whatever is currently in localStorage. logout()/switchToAccount() call
  // this to deactivate the OUTGOING user's device token -- by the time this
  // promise's request actually goes out, localStorage's authToken may already
  // be cleared or swapped to a different user (2026-08-15 Notifications
  // red-team fix, CRITICAL).
  unregister: (token: string, authTokenOverride?: string) =>
    authTokenOverride
      ? api.post('/device-tokens/unregister', { token }, { headers: { Authorization: `Bearer ${authTokenOverride}` } })
      : api.post('/device-tokens/unregister', { token }),

  getDevices: () =>
    api.get('/device-tokens/devices'),
};

// Web Push API (VAPID-based — primary, works on iOS Safari 16.4+)
export const webPushAPI = {
  getVapidPublicKey: () => api.get('/web-push/vapid-public-key'),

  subscribe: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    api.post('/web-push/subscribe', { subscription }),

  // authTokenOverride: see deviceTokenAPI.unregister's doc comment -- same
  // outgoing-user-token-capture requirement.
  unsubscribe: (endpoint: string, authTokenOverride?: string) =>
    authTokenOverride
      ? api.post('/web-push/unsubscribe', { endpoint }, { headers: { Authorization: `Bearer ${authTokenOverride}` } })
      : api.post('/web-push/unsubscribe', { endpoint }),

  getSubscriptions: () => api.get('/web-push/subscriptions'),

  sendTest: () => api.post('/web-push/test'),

  unsubscribeAll: () => api.delete('/web-push/unsubscribe-all'),
};

// Support API
export const supportAPI = {
  // Get FAQ list
  getFaqs: () => api.get('/support/faqs'),

  // Get changelog/release notes
  getChangelog: () => api.get('/support/changelog'),

  // Submit support request
  submitSupportRequest: (data: FormData | Record<string, string>) =>
    api.post('/support/request', data),

  // Submit bug report
  submitBugReport: (data: FormData | Record<string, string>) =>
    api.post('/support/bug', data),

  // Submit feature request
  submitFeatureRequest: (data: {
    title: string;
    description: string;
    useCase?: string;
    importance: 'nice_to_have' | 'important' | 'critical';
  }) => api.post('/support/feature', data),

  // Submit general feedback
  submitFeedback: (data: {
    rating: number;
    comments: string;
    allowContact: boolean;
  }) => api.post('/support/feedback', data),
};

// Legal acceptance API
export const legalAPI = {
  // Check if user needs to accept updated legal documents
  checkStatus: () => api.get('/users/me/legal-status'),

  // Record user acceptance of current ToS and Privacy Policy
  recordAcceptance: () => api.post('/users/me/legal-acceptance'),

  // Get user's legal document acceptance history
  getHistory: () => api.get('/users/me/legal-history'),
};

// Data export API (GDPR Article 20)
export const dataExportAPI = {
  // Request a new data export (rate limited: once per 24 hours)
  requestExport: () => api.post('/users/me/data-export'),

  // Get all data export requests for the user
  getExports: () => api.get('/users/me/data-export'),

  // Get status of a specific data export request
  getExportStatus: (exportId: string) => api.get(`/users/me/data-export/${exportId}`),

  // Download completed export.
  //
  // Fetches the export as a blob through the standard Authorization-header
  // axios client, then triggers a client-side download via an object URL.
  // The JWT is NEVER placed in the URL query string (that leaked the live
  // bearer token into nginx logs + browser history, and also 401'd because
  // the backend only reads the Authorization header). Spec 279 / S6.
  downloadExport: async (exportId: string) => {
    const response = await api.get(
      `/users/me/data-export/${exportId}/download`,
      { responseType: 'blob' }
    );

    // Prefer the server-provided filename (Content-Disposition), else default.
    const disposition: string =
      response.headers?.['content-disposition'] ||
      response.headers?.['Content-Disposition'] ||
      '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match
      ? decodeURIComponent(match[1])
      : `helm-data-export-${exportId}.json`;

    const blob = response.data as Blob;
    const objectUrl = URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  },
};

// Analytics API - Performance dashboard
export const analyticsAPI = {
  // Get all dashboard data
  getDashboard: (organizationId: number, period?: string) =>
    api.get('/analytics/dashboard', { params: { organizationId, period } }),

  // Get performance summary
  getSummary: (organizationId: number, period?: 'day' | 'week' | 'month' | 'all_time') =>
    api.get('/analytics/summary', { params: { organizationId, period } }),

  // Get completion rates over time
  getCompletionRates: (organizationId: number, days?: number) =>
    api.get('/analytics/completion-rates', { params: { organizationId, days } }),

  // Get category breakdown
  getCategories: (organizationId: number) =>
    api.get('/analytics/categories', { params: { organizationId } }),

  // Get time to completion stats
  getTimeToCompletion: (organizationId: number) =>
    api.get('/analytics/time-to-completion', { params: { organizationId } }),

  // Get peak performance times
  getPeakTimes: (organizationId: number) =>
    api.get('/analytics/peak-times', { params: { organizationId } }),

  // Get consequence/reward ratio
  getConsequenceRewardRatio: (organizationId: number) =>
    api.get('/analytics/consequence-reward-ratio', { params: { organizationId } }),

  // Get growth trends
  getGrowth: (organizationId: number, periods?: number) =>
    api.get('/analytics/growth', { params: { organizationId, periods } }),
};

// Praise API - Quick praise/affirmation feature
export const praiseAPI = {
  // Get available templates
  getTemplates: () => api.get('/praise/templates'),

  // Create custom template
  createTemplate: (data: { title: string; message: string; emoji?: string }) =>
    api.post('/praise/templates', data),

  // Update custom template
  updateTemplate: (templateId: number, data: { title?: string; message?: string; emoji?: string }) =>
    api.put(`/praise/templates/${templateId}`, data),

  // Delete custom template
  deleteTemplate: (templateId: number) => api.delete(`/praise/templates/${templateId}`),

  // Send praise to a user
  send: (data: { recipientId: string | number; message: string; templateId?: number; awardPoints?: boolean; points?: number; mediaUrl?: string; mediaType?: 'image' | 'video' }) =>
    api.post('/praise/send', data),

  // Send praise to multiple users (batch — N+1 elimination)
  sendBatch: (userIds: string[], message: string) =>
    api.post('/praise/send-batch', { userIds, message }),

  // Get received praise inbox
  getInbox: (params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
    api.get('/praise/inbox', { params }),

  // Get sent praise history
  getSent: (params?: { page?: number; limit?: number }) =>
    api.get('/praise/sent', { params }),

  // Get single praise by ID
  getById: (praiseId: number) => api.get(`/praise/${praiseId}`),

  // Mark praise as read
  markRead: (praiseId: number) => api.post(`/praise/${praiseId}/read`),

  // Mark all praise as read
  markAllRead: () => api.post('/praise/mark-all-read'),

  // Get praise stats
  getStats: () => api.get('/praise/stats'),

  // Get unread count
  getUnreadCount: () => api.get('/praise/unread-count'),

  // Get recently praised users (for suggestions)
  getRecentRecipients: (limit?: number) =>
    api.get('/praise/recent-recipients', { params: { limit } }),
};

// Mobile Privacy API (hidden notifications, app lock)
export const mobilePrivacyAPI = {
  get: () => api.get('/users/me/mobile-privacy'),
  update: (settings: { hiddenNotifications: boolean }) =>
    api.put('/users/me/mobile-privacy', settings),
};

// Report API - Content reporting for safety/moderation
export const reportAPI = {
  // Submit a content report
  create: (data: {
    reportedContentType: 'task' | 'comment' | 'media' | 'profile' | 'journal' | 'message' | 'channel_post' | 'channel_comment';
    reportedContentId: string | number;
    category: 'harassment' | 'illegal_content' | 'self_harm' | 'impersonation' | 'doxxing' | 'threats' | 'non_consensual_content' | 'spam' | 'other';
    description?: string;
    reportedUserId?: string | number;
  }) => api.post('/reports', data),
};
